// src/pages/Liquidate.js
import React, { useState, useEffect } from 'react';
import {
  getPositions,
  liquidatePosition,
  liquidateAllPositions,
  STRATEGY_NAMES,
  getSymbolStrategy
} from '../services/api';
import './Liquidate.css';

const PAGE_SIZE = 5;

const strategyColors = {
  vwap: '#00d4ff',
  meanrev: '#00ff88',
  momentum: '#ffd93d'
};

const Liquidate = () => {
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Per-position liquidation qty inputs
  const [liquidateQty, setLiquidateQty] = useState({});

  // Confirmation state per position: null | 'confirm' | 'loading' | 'success' | 'error'
  const [positionStatus, setPositionStatus] = useState({});
  const [positionError, setPositionError] = useState({});

  // Emergency liquidation state
  const [emergencyStep, setEmergencyStep] = useState('idle'); // idle | confirm | loading | success | error
  const [emergencyError, setEmergencyError] = useState('');
  const [emergencyResult, setEmergencyResult] = useState(null);

  useEffect(() => {
    loadPositions();
    const interval = setInterval(loadPositions, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPositions = async () => {
    try {
      const data = await getPositions();
      setPositions(data.positions);
      if (data.positions.length > 0 && !selectedPosition) {
        setSelectedPosition(data.positions[0]);
      }
      // Initialise qty inputs for any new positions
      setLiquidateQty(prev => {
        const next = { ...prev };
        data.positions.forEach(pos => {
          if (!(pos.symbol in next)) {
            next[pos.symbol] = pos.qty;
          }
        });
        return next;
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading positions:', error);
      setLoading(false);
    }
  };

  const filteredPositions = selectedStrategy === 'all'
    ? positions
    : positions.filter(pos => getSymbolStrategy(pos.symbol) === selectedStrategy);

  const totalPages = Math.max(1, Math.ceil(filteredPositions.length / PAGE_SIZE));
  const pagedPositions = filteredPositions.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // --- Single position liquidation ---
  const handleLiquidateClick = (symbol) => {
    setPositionStatus(prev => ({ ...prev, [symbol]: 'confirm' }));
  };

  const handleLiquidateCancel = (symbol) => {
    setPositionStatus(prev => ({ ...prev, [symbol]: null }));
  };

  const handleLiquidateConfirm = async (symbol) => {
    const pos = positions.find(p => p.symbol === symbol);
    const qty = parseInt(liquidateQty[symbol], 10);
    const isFullClose = qty >= pos.qty;

    setPositionStatus(prev => ({ ...prev, [symbol]: 'loading' }));
    try {
      await liquidatePosition(symbol, isFullClose ? null : qty);
      setPositionStatus(prev => ({ ...prev, [symbol]: 'success' }));
      // Refresh after 1.5 s so the user sees the success state
      setTimeout(() => {
        setPositionStatus(prev => ({ ...prev, [symbol]: null }));
        loadPositions();
        if (selectedPosition?.symbol === symbol && isFullClose) {
          setSelectedPosition(null);
        }
      }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Liquidation failed';
      setPositionError(prev => ({ ...prev, [symbol]: msg }));
      setPositionStatus(prev => ({ ...prev, [symbol]: 'error' }));
      setTimeout(() => {
        setPositionStatus(prev => ({ ...prev, [symbol]: null }));
        setPositionError(prev => ({ ...prev, [symbol]: '' }));
      }, 3000);
    }
  };

  // --- Emergency liquidation ---
  const handleEmergencyClick = () => {
    setEmergencyStep('confirm');
  };

  const handleEmergencyCancel = () => {
    setEmergencyStep('idle');
  };

  const handleEmergencyConfirm = async () => {
    setEmergencyStep('loading');
    try {
      const result = await liquidateAllPositions();
      setEmergencyResult(result);
      setEmergencyStep('success');
      setTimeout(() => {
        setEmergencyStep('idle');
        setEmergencyResult(null);
        loadPositions();
        setSelectedPosition(null);
      }, 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Emergency liquidation failed';
      setEmergencyError(msg);
      setEmergencyStep('error');
      setTimeout(() => {
        setEmergencyStep('idle');
        setEmergencyError('');
      }, 4000);
    }
  };

  // ---- Render helpers ----

  const renderActionButtons = (symbol) => {
    const status = positionStatus[symbol];
    const pos = positions.find(p => p.symbol === symbol);
    const qty = parseInt(liquidateQty[symbol], 10) || 0;
    const isFullClose = pos && qty >= pos.qty;

    if (status === 'loading') {
      return <span className="liq-status loading">Submitting order...</span>;
    }
    if (status === 'success') {
      return <span className="liq-status success">Order submitted</span>;
    }
    if (status === 'error') {
      return <span className="liq-status error">{positionError[symbol]}</span>;
    }
    if (status === 'confirm') {
      return (
        <div className="liq-confirm-row">
          <span className="liq-confirm-text">
            Sell {qty} share{qty !== 1 ? 's' : ''} of {symbol} at market?
          </span>
          <button
            className="liq-btn confirm"
            onClick={() => handleLiquidateConfirm(symbol)}
          >
            Confirm
          </button>
          <button
            className="liq-btn cancel"
            onClick={() => handleLiquidateCancel(symbol)}
          >
            Cancel
          </button>
        </div>
      );
    }
    return (
      <button
        className={`liq-btn ${isFullClose ? 'full' : 'partial'}`}
        onClick={() => handleLiquidateClick(symbol)}
        disabled={!qty || qty < 1}
      >
        {isFullClose ? 'Close Position' : `Sell ${qty}`}
      </button>
    );
  };

  const renderDetailPanel = () => {
    if (!selectedPosition) return null;
    const pos = selectedPosition;
    const symbol = pos.symbol;
    const strategy = getSymbolStrategy(symbol);
    const maxQty = pos.qty;
    const currentQty = parseInt(liquidateQty[symbol], 10) || maxQty;
    const estimatedProceeds = (currentQty * pos.current_price).toFixed(2);

    return (
      <div className="liq-detail-panel">
        <div className="liq-detail-header">
          <div className="liq-detail-title">
            <h2>{symbol}</h2>
            <span className="liq-detail-strategy" style={{ color: strategyColors[strategy] }}>
              {STRATEGY_NAMES[strategy]}
            </span>
          </div>
          <div className={`liq-detail-pnl ${pos.unrealized_pl >= 0 ? 'positive' : 'negative'}`}>
            {pos.unrealized_pl >= 0 ? '+' : ''}${pos.unrealized_pl.toFixed(2)}&nbsp;
            ({(pos.unrealized_plpc * 100).toFixed(2)}%)
          </div>
        </div>

        <div className="liq-detail-stats">
          <div className="liq-stat">
            <span className="liq-stat-label">Held Quantity</span>
            <span className="liq-stat-value">{pos.qty} shares</span>
          </div>
          <div className="liq-stat">
            <span className="liq-stat-label">Avg Entry</span>
            <span className="liq-stat-value">${pos.avg_entry_price.toFixed(2)}</span>
          </div>
          <div className="liq-stat">
            <span className="liq-stat-label">Current Price</span>
            <span className="liq-stat-value">${pos.current_price.toFixed(2)}</span>
          </div>
          <div className="liq-stat">
            <span className="liq-stat-label">Market Value</span>
            <span className="liq-stat-value">
              ${pos.market_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="liq-stat">
            <span className="liq-stat-label">Cost Basis</span>
            <span className="liq-stat-value">
              ${pos.cost_basis.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="liq-stat">
            <span className="liq-stat-label">Today's Change</span>
            <span className={`liq-stat-value ${pos.change_today >= 0 ? 'positive' : 'negative'}`}>
              {pos.change_today >= 0 ? '+' : ''}{(pos.change_today * 100).toFixed(2)}%
            </span>
          </div>
        </div>

        <div className="liq-form">
          <h3>Liquidate Position</h3>

          <div className="liq-qty-group">
            <label className="liq-qty-label">Shares to sell</label>
            <div className="liq-qty-controls">
              <input
                type="number"
                className="liq-qty-input"
                min={1}
                max={maxQty}
                value={liquidateQty[symbol] ?? maxQty}
                onChange={(e) => {
                  const v = Math.min(maxQty, Math.max(1, parseInt(e.target.value, 10) || 1));
                  setLiquidateQty(prev => ({ ...prev, [symbol]: v }));
                }}
              />
              <button
                className="liq-qty-preset"
                onClick={() => setLiquidateQty(prev => ({ ...prev, [symbol]: Math.floor(maxQty * 0.25) || 1 }))}
              >25%</button>
              <button
                className="liq-qty-preset"
                onClick={() => setLiquidateQty(prev => ({ ...prev, [symbol]: Math.floor(maxQty * 0.5) || 1 }))}
              >50%</button>
              <button
                className="liq-qty-preset"
                onClick={() => setLiquidateQty(prev => ({ ...prev, [symbol]: Math.floor(maxQty * 0.75) || 1 }))}
              >75%</button>
              <button
                className="liq-qty-preset full"
                onClick={() => setLiquidateQty(prev => ({ ...prev, [symbol]: maxQty }))}
              >100%</button>
            </div>
            <input
              type="range"
              className="liq-qty-slider"
              min={1}
              max={maxQty}
              value={liquidateQty[symbol] ?? maxQty}
              onChange={(e) => setLiquidateQty(prev => ({ ...prev, [symbol]: parseInt(e.target.value, 10) }))}
            />
            <div className="liq-qty-info">
              <span>{currentQty} of {maxQty} shares</span>
              <span className="liq-proceeds">Est. proceeds: <strong>${estimatedProceeds}</strong></span>
            </div>
          </div>

          <div className="liq-action">
            {renderActionButtons(symbol)}
          </div>
        </div>
      </div>
    );
  };

  // ---- Main render ----

  if (loading) {
    return <div className="loading">Loading positions...</div>;
  }

  const totalLiqValue = filteredPositions.reduce((s, p) => s + p.market_value, 0);
  const totalLiqPnL = filteredPositions.reduce((s, p) => s + p.unrealized_pl, 0);

  return (
    <div className="liq-page">
      {/* Header */}
      <div className="liq-page-header">
        <h1>Liquidate Positions</h1>

        <div className="liq-header-controls">
          <div className="strategy-selector">
            <label>Strategy:</label>
            <select
              value={selectedStrategy}
              onChange={(e) => {
                const s = e.target.value;
                setSelectedStrategy(s);
                setCurrentPage(1);
                const next = s === 'all'
                  ? positions
                  : positions.filter(p => getSymbolStrategy(p.symbol) === s);
                setSelectedPosition(next.length > 0 ? next[0] : null);
              }}
              className="strategy-dropdown"
            >
              <option value="all">{STRATEGY_NAMES.all}</option>
              <option value="vwap">{STRATEGY_NAMES.vwap}</option>
              <option value="meanrev">{STRATEGY_NAMES.meanrev}</option>
              <option value="momentum">{STRATEGY_NAMES.momentum}</option>
            </select>
          </div>

          {positions.length > 0 && filteredPositions.length > 0 && (
            <div className="liq-summary">
              <div className="liq-summary-item">
                <span className="liq-summary-label">Exposed Value</span>
                <span className="liq-summary-value">
                  ${totalLiqValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="liq-summary-item">
                <span className="liq-summary-label">Unrealised P&L</span>
                <span className={`liq-summary-value ${totalLiqPnL >= 0 ? 'positive' : 'negative'}`}>
                  {totalLiqPnL >= 0 ? '+' : ''}${totalLiqPnL.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Emergency button */}
          <div className="liq-emergency-wrapper">
            {emergencyStep === 'idle' && (
              <button
                className="liq-emergency-btn"
                onClick={handleEmergencyClick}
                disabled={positions.length === 0}
              >
                Emergency Liquidate All
              </button>
            )}
            {emergencyStep === 'confirm' && (
              <div className="liq-emergency-confirm">
                <span className="liq-emergency-warning">
                  Close ALL {positions.length} position{positions.length !== 1 ? 's' : ''}?
                </span>
                <button className="liq-btn confirm" onClick={handleEmergencyConfirm}>
                  Yes, liquidate all
                </button>
                <button className="liq-btn cancel" onClick={handleEmergencyCancel}>
                  Cancel
                </button>
              </div>
            )}
            {emergencyStep === 'loading' && (
              <span className="liq-status loading">Submitting all orders...</span>
            )}
            {emergencyStep === 'success' && (
              <span className="liq-status success">
                {emergencyResult?.liquidated_count} position{emergencyResult?.liquidated_count !== 1 ? 's' : ''} closed
              </span>
            )}
            {emergencyStep === 'error' && (
              <span className="liq-status error">{emergencyError}</span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      {positions.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <p>No open positions to liquidate</p>
          <span className="empty-subtitle">Positions will appear here when trades are executed</span>
        </div>
      ) : filteredPositions.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <p>No {STRATEGY_NAMES[selectedStrategy]} positions</p>
          <span className="empty-subtitle">Select "All Strategies" or switch strategy filter</span>
        </div>
      ) : (
        <div className="liq-content">
          {/* Position list */}
          <div className="liq-list">
            {pagedPositions.map((pos) => {
              const strategy = getSymbolStrategy(pos.symbol);
              const isSelected = selectedPosition?.symbol === pos.symbol;
              return (
                <div
                  key={pos.symbol}
                  className={`liq-card ${isSelected ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPosition(pos);
                    if (!(pos.symbol in liquidateQty)) {
                      setLiquidateQty(prev => ({ ...prev, [pos.symbol]: pos.qty }));
                    }
                  }}
                >
                  <div className="liq-card-top">
                    <div className="liq-card-title">
                      <span className="liq-card-symbol">{pos.symbol}</span>
                      <span className="liq-card-strategy" style={{ color: strategyColors[strategy] }}>
                        {STRATEGY_NAMES[strategy]}
                      </span>
                    </div>
                    <span className={`liq-card-pnl ${pos.unrealized_pl >= 0 ? 'positive' : 'negative'}`}>
                      {pos.unrealized_pl >= 0 ? '+' : ''}${pos.unrealized_pl.toFixed(2)}
                    </span>
                  </div>
                  <div className="liq-card-details">
                    <div className="liq-card-row">
                      <span className="liq-card-label">Qty</span>
                      <span className="liq-card-value">{pos.qty}</span>
                    </div>
                    <div className="liq-card-row">
                      <span className="liq-card-label">Price</span>
                      <span className="liq-card-value">${pos.current_price.toFixed(2)}</span>
                    </div>
                    <div className="liq-card-row">
                      <span className="liq-card-label">Value</span>
                      <span className="liq-card-value">
                        ${pos.market_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="liq-card-row">
                      <span className="liq-card-label">Return</span>
                      <span className={`liq-card-value ${pos.unrealized_plpc >= 0 ? 'positive' : 'negative'}`}>
                        {(pos.unrealized_plpc * 100).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="positions-pagination">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage === 1}
                >‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`page-btn ${page === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >{page}</button>
                ))}
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage === totalPages}
                >›</button>
              </div>
            )}
          </div>

          {/* Detail / action panel */}
          {renderDetailPanel()}
        </div>
      )}
    </div>
  );
};

export default Liquidate;
